import React, { useCallback } from "react"
import { Form, Formik, FormikHelpers } from "formik"
import { BodyCopyMedium, Box } from "@theguarantors/ui-kit-v2/dist/design-system/typography"
import { Link } from "../../LandingPage.styles"
import { TextField, Row, Col, ValidationProvider } from "@theguarantors/ui-kit-v2"
import { AddressFormValues, FormPropsInjected } from "./AddressForm.types"
import { validationSchema } from "./AddressForm.validate"
import config from "../../../../config"
import { SubmitButton } from "../../../styles"
import { BuildingsAutocompleteField } from "./components/BuildingsAutocompleteField.container"
import { generateQueryByBuildingName, goToSignUpPage } from "./AddressForm.utils"
import { useSelector } from "react-redux"
import { LGQuestionnaireStore } from "../../../../types/store.types"
import { Building } from "@theguarantors/frontend-core/dist/features/buildings"
import { FeatureFlag, useFeatureFlag } from "../../../../hooks/use-feature-flag"
import { colors } from "@theguarantors/ui-kit-v2/dist/design-system/colors"
import { Version } from "@theguarantors/ui-kit-v2/dist/utils/form"
import { BuildingAddressContainer } from "./AddressForm.styles"

const AddressFormC: React.FC<FormPropsInjected> = ({ submitForm }) => {
  const [rebrandV2MonolithReact] = useFeatureFlag(FeatureFlag.REBRAND_V2_MONOLITH_REACT)
  const rebrandV2MonolithReactFeatureFlag: Boolean = rebrandV2MonolithReact === "on"

  const [newGetStartedPage] = useFeatureFlag(FeatureFlag.NEW_GET_STARTED_PAGE)
  const newGetStartedPageFeatureFlag: boolean = newGetStartedPage === "on"

  if (newGetStartedPageFeatureFlag) {
    return (
      <Box zIndex={2} width={["85%", "85%", "85%", "54.2rem"]}>
        <Form noValidate data-testid="renters-address-form">
          <Row vgutter={["s", "xl"]}>
            <Col>
              <BuildingAddressContainer className="fs-unmask">
                <BuildingsAutocompleteField name="buildingAddress" placeholder="Building Address" />
              </BuildingAddressContainer>
            </Col>
          </Row>
          <Row vgutter={["s", "xl"]}>
            <Col>
              <div className="fs-unmask">
                <TextField
                  name="apartmentNumber"
                  placeholder="Unit #"
                  version={newGetStartedPageFeatureFlag ? Version.v2 : undefined}
                />
              </div>
            </Col>
          </Row>
          <BodyCopyMedium size="s" textAlign="center" pt="2rem" pb="2rem" fontFamily="acid" color={colors.v2_black}>
            Haven’t found an apartment yet?{` `}
            <Link to={config.noApartmentPath} color="inherit" textDecoration="underlined" fontWeight="400">
              Click Here.
            </Link>
          </BodyCopyMedium>
          <Box textAlign="center">
            <SubmitButton
              variant={rebrandV2MonolithReactFeatureFlag ? "v2_primary" : "primary"}
              type="submit"
              onClick={submitForm}
              newGetStartedPageFeatureFlag={newGetStartedPageFeatureFlag}
              fontFamily="acid"
            >
              Get Started
            </SubmitButton>
          </Box>
        </Form>
      </Box>
    )
  }

  return (
    <Box zIndex={2} width={["85%", "85%", "85%", "54.2rem"]}>
      <Form noValidate data-testid="renters-address-form">
        <Row vgutter={["s", "xl"]}>
          <Col>
            <BuildingAddressContainer className="fs-unmask">
              <BuildingsAutocompleteField name="buildingAddress" placeholder="Your Next Building Address" />
            </BuildingAddressContainer>
          </Col>
        </Row>
        <Row vgutter={["s", "xl"]}>
          <Col>
            <div className="fs-unmask">
              <TextField name="apartmentNumber" placeholder="Unit #" />
            </div>
          </Col>
        </Row>
        <BodyCopyMedium size="s" textAlign="center" pt="2rem" pb="2rem">
          Haven’t found an apartment yet? <Link to={config.noApartmentPath}>Click Here.</Link>
        </BodyCopyMedium>
        <Box textAlign="center">
          <SubmitButton
            variant={rebrandV2MonolithReactFeatureFlag ? "v2_primary" : "primary"}
            type="submit"
            onClick={submitForm}
          >
            Let's Get Started
          </SubmitButton>
        </Box>
      </Form>
    </Box>
  )
}

const initialValues = {
  buildingAddress: "",
  apartmentNumber: "",
}

export const AddressForm = () => {
  const buildings = useSelector((state: LGQuestionnaireStore) => state.buildings.list)

  const createSubmitHandler = useCallback(
    (buildings: Building[]) =>
      (
        { buildingAddress: buildingName, apartmentNumber: unit }: AddressFormValues,
        { setFieldError }: FormikHelpers<AddressFormValues>
      ) => {
        try {
          if (window && window.analytics) {
            window.analytics.track("Get Started Clicked - Get Qualified Now Page", { error: false })
          }
          goToSignUpPage(generateQueryByBuildingName(buildings, buildingName, unit))
        } catch (error) {
          if (window && window.analytics) {
            window.analytics.track("Get Started Clicked - Get Qualified Now Page", { error: true })
          }
          const message = (error as unknown as Error).message || "Something went wrong"
          setFieldError("buildingAddress", message)
        }
      },
    []
  )
  return (
    <ValidationProvider value={validationSchema}>
      <Formik
        initialValues={initialValues}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={createSubmitHandler(buildings)}
        component={AddressFormC}
      />
    </ValidationProvider>
  )
}
